import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateSachkosten, useUpdateSachkosten } from '@/api/useSachkosten';
import { MoneyInput } from '@/components/MoneyInput';
import type { SachkostenRow, UUID } from '@/api/types';
import { describeApiError } from './utils';

interface SachkostenEditDialogProps {
  opened: boolean;
  planId: UUID;
  position: SachkostenRow | null;
  /** Sammelpunkt, unter dem eine NEUE Position entsteht
   *  ("besondere_sach" / "besondere_personal"). Beim Bearbeiten ungenutzt. */
  bereichCode?: string | null;
  /** Aufgeloeste Katalog-Bezeichnung der Position (nur bei Katalog-Konten
   *  gesetzt) — zeigt der Nutzerin, welchem Punkt das freie `art`-Feld
   *  untergeordnet ist, statt des technischen `konto_code`. */
  katalogBezeichnung?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  art: string;
  betrag: number | null;
}

/**
 * Anlegen und Bearbeiten einer eigenen Kostenposition — zwei Felder.
 *
 * Die frueheren Felder fuer zeitliche Verteilung (gleich/einmal/monatlich),
 * Buchungsmonat und Aktiv-Zeitraum sind mit der Verschlankung entfallen.
 * Sie waren fuer die Wirtschaftsplanung entbehrlich: Was zaehlt, ist der
 * Jahresbetrag. Die Verteilung wirkte sich nur darauf aus, in welchem Monat
 * eine Ausgabe innerhalb desselben Jahres verbucht wird — die Jahressumme
 * war in jedem Modus dieselbe.
 *
 * Bestehende Positionen behalten ihre gespeicherte Verteilung; dieser
 * Dialog laesst sie unberuehrt, statt sie beim Speichern zu ueberschreiben.
 */
export function SachkostenEditDialog({
  opened,
  planId,
  position,
  bereichCode,
  katalogBezeichnung,
  onClose,
  onSaved,
}: SachkostenEditDialogProps) {
  const { t } = useTranslation(['sachkosten', 'common', 'errors']);
  const createSach = useCreateSachkosten(planId);
  const updateSach = useUpdateSachkosten(planId);
  const isEdit = position !== null;

  // Der Beispieltext haengt am Sammelpunkt: unter "Besondere Kosten" der
  // Personalkosten sind andere Beispiele hilfreich als bei den Sachkosten.
  const zielBereich = isEdit ? position?.bereich_code ?? null : bereichCode ?? null;
  const platzhalter =
    zielBereich === 'besondere_personal'
      ? t('sachkosten:form.field.artPlaceholderPersonal')
      : t('sachkosten:form.field.artPlaceholderSach');

  const form = useForm<FormValues>({
    initialValues: { art: '', betrag: null },
    validate: {
      art: (v) => (v.trim().length > 0 ? null : t('sachkosten:validation.artRequired')),
      betrag: (v) =>
        v !== null && v >= 0 ? null : t('sachkosten:validation.betragPositive'),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      art: position?.art ?? '',
      betrag: position ? Number(position.betrag_jahr) : null,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, position?.id]);

  const speichern = form.onSubmit(async (values) => {
    const betrag = values.betrag ?? 0;
    try {
      if (position) {
        await updateSach.mutateAsync({
          id: position.id,
          patch: { art: values.art.trim(), betrag_jahr: betrag },
        });
        notifications.show({ color: 'teal', message: t('sachkosten:info.updated') });
      } else {
        await createSach.mutateAsync({
          art: values.art.trim(),
          bereich_code: zielBereich,
          betrag_jahr: betrag,
          zeitliche_verteilung: {},
        });
        notifications.show({ color: 'teal', message: t('sachkosten:info.created') });
      }
      onSaved();
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeApiError(err) || t('errors:generic'),
      });
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('sachkosten:form.edit.title') : t('sachkosten:form.create.title')}
      centered
    >
      <form onSubmit={speichern}>
        <Stack>
          <TextInput
            label={t('sachkosten:form.field.art')}
            placeholder={platzhalter}
            description={
              katalogBezeichnung
                ? t('sachkosten:form.field.artKatalogHint', {
                    bezeichnung: katalogBezeichnung,
                  })
                : undefined
            }
            {...form.getInputProps('art')}
          />
          <MoneyInput
            label={t('sachkosten:form.field.betragJahr')}
            value={form.values.betrag}
            onChange={(v) => form.setFieldValue('betrag', v)}
            error={form.errors.betrag}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={createSach.isPending || updateSach.isPending}>
              {t('common:actions.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
